import { skipToken } from '@reduxjs/toolkit/query'

import { queryView } from '@krp/client-runtime'
import {
  aqsiParkedSales,
  aqsiSale,
  aqsiSaleOperation,
  BackgroundReceipts,
  BackgroundReceiptCard,
  BackgroundReceiptDone,
  FAST_DONE_SHOWN_MS,
  formatKopecks,
  useClockBucket,
  type AqsiSale,
  type FastReceiptNote,
} from '@krp/resource-aqsi-ui'

const NO_NOTES: readonly FastReceiptNote[] = []

/** Чтение сохранённых продаж кассы; ни оплаты, ни новых команд чека отсюда нет. */
export function AqsiBackgroundReceiptsBinding({ resourceId, deviceId, activeSaleId = null, onOpen, onResolve, onLatePayment, latePaymentBusy, onReturn, fastNotes = NO_NOTES, onFastDismiss }: {
  resourceId: string
  deviceId: number
  /** Продажа кассы сейчас: её оплату и поздний слип ведёт окно оплаты — в «Чеках в фоне» её нет (RP-PAY-ONE). */
  activeSaleId?: string | null
  onOpen: () => void
  onResolve?: (operationId: string) => void
  /** Решение по запоздавшему слипу отложенной продажи; нет - кнопок «Оплаты не было» / «Оплата прошла» нет. */
  onLatePayment?: (sale: AqsiSale, paid: boolean) => void
  latePaymentBusy?: boolean
  /** Чек с проблемой: вернуть продажу на кассу и решить её в окне оплаты. */
  onReturn?: (sale: AqsiSale) => void
  /** Быстрая оплата: продажи, ушедшие в фон в этой вкладке (память вкладки), и «Понятно» у «Чек пробит». */
  fastNotes?: readonly FastReceiptNote[]
  onFastDismiss?: (saleId: string) => void
}) {
  const now = useClockBucket(1_000)
  const doneNotes = fastNotes.filter((note) => note.doneAt !== null && now - Date.parse(note.doneAt) < FAST_DONE_SHOWN_MS)
  const result = aqsiParkedSales.useQuery({ resourceId, deviceId }, { pollingInterval: 5_000, refetchOnMountOrArgChange: true })
  const view = queryView({ ...result, data: result.currentData, isLoading: result.isFetching }, (rows) => rows.length === 0)
  return <BackgroundReceipts query={view} onReload={() => { void result.refetch() }} onOpen={onOpen}
    extra={doneNotes.length === 0 ? null : doneNotes.map((note) => <DoneReceipt key={note.saleId} resourceId={resourceId} saleId={note.saleId}
      onDismiss={() => onFastDismiss?.(note.saleId)} />)}>
    {result.currentData?.filter((sale) => activeSaleId === null || sale.id.toLowerCase() !== activeSaleId.toLowerCase()).map((sale) => <ReceiptBinding key={sale.id} resourceId={resourceId} sale={sale} onResolve={onResolve} onLatePayment={onLatePayment} latePaymentBusy={latePaymentBusy}
      onReturn={onReturn === undefined ? undefined : () => onReturn(sale)} />)}
  </BackgroundReceipts>
}

function DoneReceipt({ resourceId, saleId, onDismiss }: { resourceId: string; saleId: string; onDismiss: () => void }) {
  const result = aqsiSale.useQuery({ resourceId, saleId })
  const sale = result.currentData
  return <BackgroundReceiptDone totalText={sale === undefined ? '' : formatKopecks(sale.totalKopecks)} receiptId={sale?.receiptId ?? null} onDismiss={onDismiss} />
}

function ReceiptBinding({ resourceId, sale, onResolve, onLatePayment, latePaymentBusy, onReturn }: {
  resourceId: string
  sale: AqsiSale
  onResolve: ((operationId: string) => void) | undefined
  onLatePayment: ((sale: AqsiSale, paid: boolean) => void) | undefined
  latePaymentBusy: boolean | undefined
  onReturn: (() => void) | undefined
}) {
  const now = useClockBucket(1_000)
  const checkingPayment = ['InProgress', 'Unknown', 'Failed'].includes(sale.payment)
  const operationId = checkingPayment ? sale.purchaseOperationId : sale.receiptOperationId
  const result = aqsiSaleOperation.useQuery(operationId === null ? skipToken : { resourceId, operationId },
    { pollingInterval: sale.payment === 'Failed' ? 0 : checkingPayment ? 1_000 : 5_000, refetchOnMountOrArgChange: true })
  return <BackgroundReceiptCard sale={sale} operation={result.currentData} query={operationId === null ? null : queryView({ ...result, data: result.currentData, isLoading: result.isFetching }, () => false)} now={now} onReload={() => { void result.refetch() }}
    onResolve={operationId !== null && onResolve !== undefined ? () => onResolve(operationId) : undefined}
    onLatePayment={onLatePayment === undefined ? undefined : (paid) => onLatePayment(sale, paid)} latePaymentBusy={latePaymentBusy} onReturn={onReturn} />
}
